<template>
    <div
        :class="gradientAndFocusClasses.gradientClass"
        class="relative w-full overflow-hidden rounded-12 bg-black/5 from-transparent to-accent/20 to-70% p-3 md:aspect-square">
        <div
            :class="gradientAndFocusClasses.focusClass"
            class="absolute h-1/2 w-1/2 -rotate-12 rounded-full bg-accent/50 blur-[5rem]" />
        <div class="relative flex h-full flex-col justify-between">
            <div
                class="flex grow items-center justify-center px-5 text-48/100 font-thin leading-[114%] text-white max-md:my-6 md:text-[2.5rem] lg:text-[5rem] xl:text-[6.25rem]">
                <div class="relative">
                    <span class="opacity-0">{{ count }}</span>
                    <BaseCounter
                        :count="count"
                        :duration="counterDuration"
                        class="absolute left-0 top-0" />
                </div>
                <div class="flex items-center">
                    <span v-if="title">&nbsp;{{ title }}</span>
                    <InNumbersPeopleSVG v-if="icon === 'people'" />
                    <InNumbersPlusSVG v-else-if="icon === 'plus'" />
                    <InNumbersSuccessSVG v-else />
                </div>
            </div>
            <p
                v-html="description"
                class="text-center text-16/150 font-medium text-accent-100 md:h-30 md:text-left md:text-ash-200 lg:text-20/120 lg:font-semibold xl:text-24/133" />
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseCounter from '~/components/base/base-counter/BaseCounter.vue'
import type {
    TInNumbersGradient,
    TInNumbersIcons,
} from '~/components/particles/in-numbers/in-numbers.types'
import InNumbersPeopleSVG from '~/components/particles/in-numbers/svg/InNumbersPeopleSVG.vue'
import InNumbersPlusSVG from '~/components/particles/in-numbers/svg/InNumbersPlusSVG.vue'
import InNumbersSuccessSVG from '~/components/particles/in-numbers/svg/InNumbersSuccessSVG.vue'

const props = defineProps({
    gradient: {
        type: String as PropType<TInNumbersGradient>,
        default: 'bottom-right',
    },
    count: {
        type: Number as PropType<number>,
        default: 0,
    },
    title: {
        type: [String, null] as PropType<string | null>,
        default: '',
    },
    description: {
        type: [String, null] as PropType<string | null>,
        default: '',
    },
    icon: {
        type: String as PropType<TInNumbersIcons>,
        default: null,
    },
    counterDuration: {
        type: Number as PropType<number>,
        default: 3500,
    },
})

const gradientAndFocusClasses = computed(() => {
    const result = {
        gradientClass: '',
        focusClass: '',
    }

    switch (props.gradient) {
        case 'bottom-right': {
            result.gradientClass = 'bg-gradient-to-br'
            result.focusClass = '-bottom-1/4 -right-[10%]'
            break
        }
        case 'top-right': {
            result.gradientClass = 'bg-gradient-to-tr'
            result.focusClass = '-right-[10%] -top-1/4'
            break
        }
        case 'bottom': {
            result.gradientClass = 'bg-gradient-to-b'
            result.focusClass = '-bottom-1/4 left-1/2 -translate-x-1/2'
            break
        }
    }

    return result
}) as ComputedRef<{
    gradientClass: string
    focusClass: string
}>
</script>
